import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { DeviceMobile, Spinner, MagnifyingGlass, Lightning } from "@phosphor-icons/react";

const FALLBACK = [
  { code: "US", name: "United States" }, { code: "NG", name: "Nigeria" },
  { code: "GH", name: "Ghana" }, { code: "KE", name: "Kenya" },
  { code: "CM", name: "Cameroon" }, { code: "ZA", name: "South Africa" },
  { code: "GB", name: "United Kingdom" }, { code: "IN", name: "India" },
];

export default function Airtime() {
  const qc = useQueryClient();
  const [country, setCountry] = useState("NG");
  const [phone, setPhone] = useState("");
  const [operator, setOperator] = useState(null);
  const [amount, setAmount] = useState(5);
  const [detecting, setDetecting] = useState(false);
  const [busy, setBusy] = useState(false);

  const { data: wallet } = useQuery({ queryKey: ["wallet"], queryFn: async () => (await api.get("/wallet")).data });
  const { data: status } = useQuery({ queryKey: ["gc-status"], queryFn: async () => (await api.get("/giftcards/status")).data });
  const { data: countryData } = useQuery({
    queryKey: ["rl-airtime-countries"],
    queryFn: async () => (await api.get("/reloadly/airtime-countries")).data,
    enabled: !!status?.configured,
  });
  const countries = countryData?.countries?.length ? countryData.countries : FALLBACK;
  const [ptype, setPtype] = useState("Airtime");
  const { data: opData, isFetching: opsLoading, isError: opsError } = useQuery({
    queryKey: ["rl-ops", country],
    queryFn: async () => (await api.get(`/reloadly/operators?country=${country}`)).data,
    enabled: !!status?.configured,
    staleTime: 600000,
  });
  const typed = (opData?.operators || []).filter((o) =>
    ptype === "Data" ? o.data : ptype === "Bundle" ? o.bundle : !o.data && !o.bundle);
  const counts = (opData?.operators || []).reduce((a, o) => {
    a[o.data ? "Data" : o.bundle ? "Bundle" : "Airtime"] += 1; return a;
  }, { Airtime: 0, Bundle: 0, Data: 0 });
  const fixed = operator?.fixed_amounts || [];
  const desc = (a) => operator?.descriptions?.[Number(a).toFixed(2)] || operator?.descriptions?.[String(a)];

  const detect = async () => {
    if (!phone) { toast.error("Enter the phone number."); return; }
    setDetecting(true); setOperator(null);
    try {
      const { data } = await api.get(`/reloadly/detect?phone=${encodeURIComponent(phone)}&country=${country}`);
      setOperator(data);
      toast.success(`Carrier: ${data.name}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setDetecting(false); }
  };

  const send = async () => {
    if (!operator || amount <= 0 || !phone) { toast.error("Detect the carrier and enter an amount."); return; }
    setBusy(true);
    try {
      await api.post("/reloadly/topup", {
        operator_id: operator.operator_id, amount_usd_cents: Math.round(amount * 100),
        recipient_number: phone, country, operator_name: operator.name,
      });
      toast.success("Top-up submitted — airtime is on its way.");
      setPhone(""); setOperator(null);
      qc.invalidateQueries({ queryKey: ["wallet"] });
      qc.invalidateQueries({ queryKey: ["ledger"] });
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (status && !status.configured) {
    return (
      <div className="space-y-6" data-testid="airtime-page">
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Airtime</h1>
        <div className="rounded-2xl border border-border bg-card p-8 text-center text-muted-foreground" data-testid="airtime-not-configured">
          Airtime is being set up. Please check back shortly.
        </div>
      </div>
    );
  }

  const presets = [5, 10, 20, 50];

  return (
    <div className="space-y-8" data-testid="airtime-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Airtime &amp; Data</h1>
          <p className="mt-2 text-muted-foreground">Recharge any phone worldwide — paid instantly from your wallet.</p>
        </div>
        <div className="rounded-2xl border border-border bg-card px-4 py-2 text-sm">
          Wallet: <span data-testid="airtime-wallet-balance" className="font-mono-metric font-semibold">{wallet ? money(wallet.balance_cents, wallet.currency) : "—"}</span>
        </div>
      </div>

      <div className="max-w-xl space-y-5 rounded-2xl border border-border bg-card p-6">
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Country</label>
          <select data-testid="airtime-country" value={country} onChange={(e) => { setCountry(e.target.value); setOperator(null); }}
            className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 text-sm">
            {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Phone number</label>
          <div className="mt-2 flex gap-2">
            <input data-testid="airtime-phone" value={phone} onChange={(e) => { setPhone(e.target.value); setOperator(null); }}
              placeholder="+234 801 234 5678"
              className="flex-1 rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none" />
            <button data-testid="airtime-detect" onClick={detect} disabled={detecting || !phone}
              className="flex items-center gap-2 rounded-xl border border-primary/40 bg-primary/5 px-4 text-sm font-semibold text-primary disabled:opacity-50">
              {detecting ? <Spinner className="animate-spin" /> : <MagnifyingGlass size={16} />} Detect
            </button>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">What to send</label>
          <div data-testid="airtime-type-filter" className="mt-2 grid grid-cols-3 gap-2">
            {[["Airtime", "Airtime"], ["Bundle", "Bundles"], ["Data", "Data only"]].map(([k, lbl]) => (
              <button key={k} data-testid={`airtime-type-${k.toLowerCase()}`} onClick={() => { setPtype(k); setOperator(null); }}
                className={`rounded-xl border py-2.5 text-sm font-semibold transition-colors ${ptype === k ? "border-primary bg-accent" : "border-border"}`}>
                {lbl} <span className="text-xs font-normal text-muted-foreground">({counts[k]})</span>
              </button>
            ))}
          </div>
          <div className="mt-3 grid max-h-64 grid-cols-2 gap-2 overflow-y-auto">
            {opsLoading && <p className="col-span-2 text-sm text-muted-foreground">Loading operators…</p>}
            {opsError && <p data-testid="airtime-ops-error" className="col-span-2 text-sm text-red-500">Couldn't load operators right now. Please try again shortly.</p>}
            {!opsLoading && !opsError && typed.length === 0 && <p data-testid="airtime-type-empty" className="col-span-2 text-sm text-muted-foreground">No {ptype.toLowerCase()} operators for this country.</p>}
            {typed.map((o) => (
              <button key={o.operator_id} data-testid={`airtime-op-${o.operator_id}`}
                onClick={() => { setOperator(o); if (o.fixed_amounts?.length) setAmount(o.fixed_amounts[0]); }}
                className={`flex items-center gap-2 rounded-xl border p-2 text-left text-xs font-medium transition-colors ${operator?.operator_id === o.operator_id ? "border-primary bg-accent" : "border-border hover:border-primary"}`}>
                {o.logo && <img src={o.logo} alt="" className="h-7 w-7 rounded object-contain" />}
                <span className="line-clamp-2">{o.name}</span>
              </button>
            ))}
          </div>
        </div>

        {operator && (
          <div className="flex items-center gap-3 rounded-xl border border-border bg-background p-3" data-testid="airtime-operator">
            {operator.logo ? <img src={operator.logo} alt={operator.name} className="h-9 w-9 rounded-lg object-contain" />
              : <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-primary"><Lightning size={18} weight="fill" /></div>}
            <div>
              <p className="text-sm font-semibold">{operator.name}</p>
              {operator.destination_currency && <p className="text-xs text-muted-foreground">Delivers in {operator.destination_currency}</p>}
            </div>
          </div>
        )}

        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{fixed.length ? "Choose a plan (USD from your wallet)" : "Amount (USD from your wallet)"}</label>
          {fixed.length > 0 ? (
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {fixed.map((a) => (
                <button key={a} data-testid={`airtime-plan-${a}`} onClick={() => setAmount(a)}
                  className={`rounded-xl border px-3 py-2.5 text-left text-sm ${amount === a ? "border-primary bg-accent" : "border-border"}`}>
                  <span className="font-mono-metric font-semibold">${a}</span>
                  {desc(a) && <span className="block text-xs text-muted-foreground">{desc(a)}</span>}
                </button>
              ))}
            </div>
          ) : (
          <div className="mt-2 flex flex-wrap gap-2">
            {presets.map((p) => (
              <button key={p} data-testid={`airtime-preset-${p}`} onClick={() => setAmount(p)}
                className={`rounded-xl border px-4 py-2.5 text-sm font-semibold ${amount === p ? "border-primary bg-accent" : "border-border"}`}>
                ${p}
              </button>
            ))}
            <input data-testid="airtime-amount" type="number" min="1" value={amount}
              onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
              className="w-28 rounded-xl border border-input bg-background px-4 py-2.5 text-sm outline-none" />
          </div>
          )}
        </div>

        <button data-testid="airtime-send" onClick={send} disabled={busy || !operator || amount <= 0}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 font-semibold text-primary-foreground transition-transform hover:scale-[1.01] disabled:opacity-60">
          {busy ? <><Spinner className="animate-spin" /> Sending…</> : <><DeviceMobile size={18} weight="fill" /> Send {money(Math.round(amount * 100))} top-up</>}
        </button>
        <p className="text-center text-xs text-muted-foreground">Detect the carrier or pick an operator above, then choose an amount.</p>
      </div>
    </div>
  );
}
